import { useState } from 'react'
import { useDataTableContext } from 'components/DataTable'
import apiService from 'services/api'
import lodash from 'lodash'
import confirm from 'components/dialogConfirm'
import { queryClient } from 'services/queryClient'
import { toast } from 'react-toastify'

interface BulkDealsDeleterProps {
  data: any[]
  accessorKey: string
  tableQueryKey: string[]
  filterParams: any
  useReportsEndpoint?: boolean
}

export function useBulkDealsDeleter(props: BulkDealsDeleterProps) {
  const api = apiService()
  const [isDeleting, setIsDeleting] = useState(false)

  const {
    state: { rowSelection, isAllRowsSelected, totalRowsInBackend },
  } = useDataTableContext()

  async function handleSubmit() {
    const selectedRows = props.data?.filter((_, i) => {
      if (isAllRowsSelected) {
        return !rowSelection?.[i]
      }
      return !!rowSelection?.[i]
    })

    const selectedDealIDs = selectedRows?.map((r: any) =>
      lodash.get(r, props.accessorKey)
    ) as number[]

    const unselectedCount = props.data?.filter((_, i) => {
      return !rowSelection?.[i]
    }).length

    const count = isAllRowsSelected
      ? totalRowsInBackend - unselectedCount
      : selectedDealIDs.length

    const confirmed = await confirm(
      `Are you sure you want to delete ${count > 1 ? `all ${count} deals` : 'this deal'}?`,
      'Delete deals',
      undefined,
      {
        confirmLabel: 'Delete',
      }
    )

    if (!confirmed) {
      return
    }

    setIsDeleting(true)
    try {
      // Use appropriate endpoint based on whether this is for reports table
      const deleteMethod = props.useReportsEndpoint
        ? api.bulkDeleteDealReports
        : api.bulkDeleteDeals
      const data = await deleteMethod(
        selectedDealIDs,
        isAllRowsSelected,
        props.filterParams
      )

      if (data?.message) {
        toast.info(
          data?.message ??
            'We are processing your request, you will be notified when it is done'
        )
      } else {
        toast.success('Deals deleted successfully')
      }

      // Wait before refetching to allow reports to process.
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Refetch both the table query and all deal card queries
      await Promise.all([
        queryClient.refetchQueries({ queryKey: props.tableQueryKey }),
        queryClient.invalidateQueries({ queryKey: ['deals'] }),
      ])
    } finally {
      setIsDeleting(false)
    }
  }

  return { handleSubmit, isDeleting }
}
